import React, { useState, useEffect } from "react";
import { Card, Table, Input, Button, Space, Tooltip, Tag, Select } from "antd";
import { EyeOutlined, DeleteOutlined } from "@ant-design/icons";
import Modal from "../../components/Modal";
import { useGet, usePost, useDynamicApi } from "../../hooks/useApi";
import { useDebounce } from "../../hooks/useDebounce";
import { API_ENDPOINTS, API_BASE_URL } from "../../config/api";
import Toast from "../../utils/toast";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const NotificationManager = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [notificationToDelete, setNotificationToDelete] = useState(null);
  const [isViewMode, setIsViewMode] = useState(false);

  // State for form data
  const [formData, setFormData] = useState({
    title: "",
    message: "",
    user_groups: [],
    specific_users: [],
  });

  // Form validation errors
  const [formErrors, setFormErrors] = useState({
    title: "",
    message: "",
    recipients: "",
  });

  // Users list for selection
  const [usersList, setUsersList] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Pagination and search states
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [notificationList, setNotificationList] = useState([]);

  // Status filter options
  const STATUS_OPTIONS = [
    { value: "", label: t("common.allStatus") },
    { value: "Sent", label: t("notificationManager.statusSent") },
    { value: "Failed", label: t("notificationManager.statusFailed") },
    { value: "Partial", label: t("notificationManager.statusPartial") },
  ];

  // User group options from your User model
  const USER_GROUP_OPTIONS = [
    { value: "INDIVIDUAL", label: t("notificationManager.groupIndividual") },
    { value: "CLUB_COACH", label: t("notificationManager.groupClubCoach") },
    { value: "CLUB_ADMIN", label: t("notificationManager.groupClubAdmin") },
    {
      value: "KNKV_PARTICIPANT",
      label: t("notificationManager.groupKNKVParticipant"),
    },
    {
      value: "KNKV_INSTRUCTOR",
      label: t("notificationManager.groupKNKVInstructor"),
    },
    { value: "SYSTEM_ADMIN", label: t("notificationManager.groupSystemAdmin") },
  ];

  // API hooks
  const {
    execute: fetchList,
    error: fetchError,
    loading: loadingList,
  } = useGet(
    `${API_ENDPOINTS.NOTIFICATION_LIST}?page=${currentPage}&page_size=${pageSize}&search=${debouncedSearchTerm}`,
  );
  const {
    post,
    error: postError,
    loading: creating,
  } = usePost(API_ENDPOINTS.NOTIFICATION_LIST);
  const {
    execute: getById,
    error: getByIdError,
    loading: loadingById,
  } = useDynamicApi("GET");
  const {
    execute: deleteData,
    error: deleteError,
    loading: deleting,
  } = useDynamicApi("DELETE");

  // Loading state
  const loading = loadingList || loadingById || deleting || creating;

  // Load data with current pagination and search
  const loadData = async () => {
    try {
      const response = await fetchList();
      if (response) {
        setNotificationList(response.data || []);
        setTotalPages(response.pagination?.total_pages || 1);
        setTotalCount(response.pagination?.total_count || 0);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  // Load users for selection
  // const loadUsers = async () => {
  //   setLoadingUsers(true);
  //   try {
  //     const response = await fetch(
  //       `${API_BASE_URL}${API_ENDPOINTS.USERS_LIST}?page_size=1000`,
  //       {
  //         headers: {
  //           Authorization: `Bearer ${getToken()}`,
  //         },
  //       },
  //     );
  //     const result = await response.json();
  //     if (result.data) {
  //       setUsersList(result.data);
  //     }
  //   } catch (error) {
  //     console.error("Error loading users:", error);
  //   } finally {
  //     setLoadingUsers(false);
  //   }
  // };
  const {
    execute: fetchUsers,
    data: usersResponse,
    loading: loadingUsersFromHook,
  } = useGet(`${API_ENDPOINTS.USERS_LIST}?page_size=1000`);

  // Remove the manual getToken() and raw fetch
  const loadUsers = async () => {
    try {
      const response = await fetchUsers();
      if (response?.data) {
        setUsersList(response.data);
      }
    } catch (error) {
      console.error("Error loading users:", error);
      Toast.error(t("common.error"));
    }
  };

  // Call it in useEffect
  useEffect(() => {
    loadUsers();
  }, []);

  // Function to get token
  const getToken = () => {
    return (
      localStorage.getItem("adminToken") ||
      localStorage.getItem("access_token") ||
      localStorage.getItem("token") ||
      sessionStorage.getItem("adminToken") ||
      sessionStorage.getItem("access_token")
    );
  };

  // Initial data fetch
  useEffect(() => {
    loadData();
    loadUsers();
  }, [currentPage, pageSize]);

  // Handle search - triggers when debounced value changes
  useEffect(() => {
    setCurrentPage(1);
    loadData();
  }, [debouncedSearchTerm]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user selects
    if (formErrors.recipients) {
      setFormErrors((prev) => ({ ...prev, recipients: "" }));
    }
  };

  // Reset form data
  const resetForm = () => {
    setFormData({
      title: "",
      message: "",
      user_groups: [],
      specific_users: [],
    });
    setFormErrors({
      title: "",
      message: "",
      recipients: "",
    });
    setSelectedNotification(null);
    setIsViewMode(false);
  };

  // Validate form function
  const validateForm = () => {
    const errors = {};

    // Title validation
    if (!formData.title.trim()) {
      errors.title = t("notificationManager.titleRequired");
    } else if (formData.title.trim().length < 3) {
      errors.title = t("notificationManager.titleMinLength");
    } else if (formData.title.trim().length > 255) {
      errors.title = t("notificationManager.titleMaxLength");
    }

    // Message validation
    if (!formData.message.trim()) {
      errors.message = t("notificationManager.messageRequired");
    } else if (formData.message.trim().length < 5) {
      errors.message = t("notificationManager.messageMinLength");
    }

    // Recipients validation - at least one group or specific users
    if (
      (!formData.user_groups || formData.user_groups.length === 0) &&
      (!formData.specific_users || formData.specific_users.length === 0)
    ) {
      errors.recipients = t("notificationManager.recipientsRequired");
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle create notification
  const handleCreate = async (e) => {
    e?.preventDefault?.();

    if (!validateForm()) {
      Toast.error(t("notificationManager.fixFormErrors"));
      return;
    }

    try {
      const response = await post(formData);

      if (response?.status === true) {
        Toast.success(
          response?.message || t("notificationManager.createdSuccess"),
        );
        await loadData();
        setIsModalOpen(false);
        resetForm();
      } else {
        Toast.error(response?.message || t("notificationManager.failedCreate"));
      }
    } catch (error) {
      console.error("Error sending notification:", error);
      Toast.error(error.message || t("notificationManager.failedCreate"));
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!notificationToDelete) return;

    try {
      const endpoint = `${API_ENDPOINTS.NOTIFICATION_UPDATE}/${notificationToDelete.id}`;
      const response = await deleteData(endpoint);

      if (response?.status === true) {
        Toast.success(
          response?.message || t("notificationManager.deletedSuccess"),
        );
        await loadData();
      } else {
        Toast.error(response?.message || t("notificationManager.failedDelete"));
      }
    } catch (error) {
      console.error("Error deleting notification:", error);
      Toast.error(error.message || t("notificationManager.failedDelete"));
    } finally {
      setIsDeleteModalOpen(false);
      setNotificationToDelete(null);
    }
  };

  // Open modal for create
  const openCreateModal = () => {
    resetForm();
    setIsViewMode(false);
    setIsModalOpen(true);
  };

  // Navigate to view page
  const openViewPage = (id) => {
    navigate(`/notifications/details/${id}`);
  };

  // Open delete confirmation modal
  const openDeleteModal = (notification) => {
    setNotificationToDelete(notification);
    setIsDeleteModalOpen(true);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === "nl" ? "nl-NL" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get status tag color
  const getStatusColor = (status) => {
    switch (status) {
      case "Sent":
        return "green";
      case "Failed":
        return "red";
      case "Partial":
        return "orange";
      default:
        return "default";
    }
  };

  // Format recipients display
  const formatRecipients = (notification) => {
    const groups = notification.selected_user_groups || [];
    const specificUsers = notification.selected_specific_users || [];
    const parts = [];

    if (groups.length > 0) {
      parts.push(`${groups.length} ${t("notificationManager.groups")}`);
    }
    if (specificUsers.length > 0) {
      parts.push(
        `${specificUsers.length} ${t("notificationManager.specificUsers")}`,
      );
    }

    return parts.join(" + ") || t("notificationManager.noRecipients");
  };

  const columns = [
    {
      title: t("notificationManager.title"),
      dataIndex: "title",
      key: "title",
      render: (title) => title || "N/A",
    },
    {
      title: t("notificationManager.messagePreview"),
      dataIndex: "message",
      key: "message",
      render: (message) =>
        message?.substring(0, 100) + (message?.length > 100 ? "..." : "") ||
        "N/A",
    },
    {
      title: t("notificationManager.recipients"),
      key: "recipients",
      render: (_, record) => (
        <Tooltip
          title={`${t("notificationManager.total")}: ${record.total_recipients || 0} ${t("notificationManager.users")}`}
        >
          <span>{formatRecipients(record)}</span>
        </Tooltip>
      ),
    },
    {
      title: t("notificationManager.totalUsers"),
      dataIndex: "total_recipients",
      key: "total_recipients",
      render: (total) => total || 0,
    },
    {
      title: t("common.status"),
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status
            ? t(`notificationManager.status${status}`)
            : t("notificationManager.statusSent")}
        </Tag>
      ),
    },
    {
      title: t("notificationManager.sentBy"),
      dataIndex: "sent_by",
      key: "sent_by",
      render: (sentBy) =>
        sentBy?.full_name || sentBy?.email || t("notificationManager.system"),
    },
    {
      title: t("notificationManager.sentAt"),
      dataIndex: "created_at",
      key: "created_at",
      render: (date) => formatDate(date),
    },
    {
      title: t("common.actions"),
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title={t("common.view")}>
            <Button
              className="btn-view"
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => openViewPage(record.id)}
            />
          </Tooltip>
          <Tooltip title={t("common.delete")}>
            <Button
              className="btn-delete"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => openDeleteModal(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card
        title={t("notificationManager.title")}
        extra={
          <Button
            type="primary"
            onClick={openCreateModal}
            disabled={loadingList}
          >
            {t("notificationManager.createNew")}
          </Button>
        }
      >
        <Input
          placeholder={t("notificationManager.searchPlaceholder")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          allowClear
          style={{ maxWidth: 400, marginBottom: 16 }}
          disabled={loadingList}
        />

        <div className="table-responsive">
          <Table
            dataSource={notificationList}
            columns={columns}
            rowKey="id"
            loading={loadingList}
            locale={{
              emptyText: searchTerm
                ? t("notificationManager.noMatchingNotifications")
                : t("notificationManager.noNotificationsFound"),
            }}
            pagination={{
              current: currentPage,
              pageSize,
              total: totalCount,
              showSizeChanger: true,
              pageSizeOptions: ["5", "10", "20", "50", "100"],
              showTotal: (total) =>
                `${t("common.showing")} ${(currentPage - 1) * pageSize + 1} ${t("common.to")} ${Math.min(currentPage * pageSize, total)} ${t("common.of")} ${total} ${t("notificationManager.notifications")}`,
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size || 10);
              },
            }}
          />
        </div>
      </Card>

      {/* Create Modal */}
      {isModalOpen && !isViewMode && (
        <Modal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            resetForm();
          }}
          title={t("notificationManager.createTitle")}
          onConfirm={handleCreate}
          confirmText={
            creating
              ? t("notificationManager.sending")
              : t("notificationManager.sendNotification")
          }
          showConfirm={true}
          size="large"
          confirmDisabled={creating}
        >
          <form className="modal-form" onSubmit={handleCreate}>
            <div className="form-group">
              <label htmlFor="title">
                {t("notificationManager.title")}{" "}
                <span className="required">*</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder={t("notificationManager.enterTitle")}
                required
                disabled={creating}
                className={formErrors.title ? "input-error" : ""}
              />
              {formErrors.title && (
                <div className="error-message">{formErrors.title}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="message">
                {t("notificationManager.message")}{" "}
                <span className="required">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder={t("notificationManager.enterMessage")}
                required
                disabled={creating}
                rows="6"
                className={formErrors.message ? "input-error" : ""}
              />
              {formErrors.message && (
                <div className="error-message">{formErrors.message}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="user_groups">
                {t("notificationManager.selectUserGroups")}
              </label>
              <Select
                mode="multiple"
                placeholder={t(
                  "notificationManager.selectUserGroupsPlaceholder",
                )}
                value={formData.user_groups}
                onChange={(value) => handleSelectChange("user_groups", value)}
                options={USER_GROUP_OPTIONS}
                style={{ width: "100%" }}
                disabled={creating || loadingUsers}
                allowClear
              />
              <small className="form-help">
                {t("notificationManager.userGroupsHelp")}
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="specific_users">
                {t("notificationManager.selectSpecificUsers")}
              </label>
              <Select
                mode="multiple"
                placeholder={t(
                  "notificationManager.selectSpecificUsersPlaceholder",
                )}
                value={formData.specific_users}
                onChange={(value) =>
                  handleSelectChange("specific_users", value)
                }
                options={usersList.map((user) => ({
                  value: user.id,
                  label: `${user.full_name || t("notificationManager.user")} (${user.email || user.mobile_number || t("notificationManager.noContact")})`,
                }))}
                style={{ width: "100%" }}
                disabled={creating || loadingUsers}
                allowClear
                showSearch
                optionFilterProp="label"
                loading={loadingUsers}
              />
              <small className="form-help">
                {t("notificationManager.specificUsersHelp")}
              </small>
            </div>

            {formErrors.recipients && (
              <div className="error-message" style={{ marginBottom: 15 }}>
                {formErrors.recipients}
              </div>
            )}

            <div className="recipient-summary">
              <h4>{t("notificationManager.recipientSummary")}:</h4>
              <p>
                <strong>{t("notificationManager.userGroups")}:</strong>{" "}
                {formData.user_groups?.length || 0}{" "}
                {t("notificationManager.selected")}
              </p>
              <p>
                <strong>{t("notificationManager.specificUsers")}:</strong>{" "}
                {formData.specific_users?.length || 0}{" "}
                {t("notificationManager.selected")}
              </p>
              <p>
                <strong>{t("notificationManager.total")}:</strong>{" "}
                {(formData.user_groups?.length || 0) +
                  (formData.specific_users?.length || 0)}{" "}
                {t("notificationManager.recipientSelections")}
              </p>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && notificationToDelete && (
        <Modal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setNotificationToDelete(null);
          }}
          title={t("notificationManager.confirmDelete")}
          onConfirm={handleDeleteConfirm}
          confirmText={deleting ? t("common.deleting") : t("common.delete")}
          confirmColor="danger"
          confirmDisabled={deleting}
        >
          <div className="delete-confirmation">
            <div className="warning-icon">⚠️</div>
            <h3>{t("notificationManager.confirmDeleteMessage")}</h3>
            <div className="item-details">
              <p>
                <strong>{t("notificationManager.title")}:</strong>{" "}
                {notificationToDelete.title}
              </p>
              <p>
                <strong>{t("notificationManager.message")}:</strong>{" "}
                {notificationToDelete.message?.substring(0, 100)}
                {notificationToDelete.message?.length > 100 ? "..." : ""}
              </p>
              <p>
                <strong>{t("notificationManager.recipients")}:</strong>{" "}
                {formatRecipients(notificationToDelete)}
              </p>
              <p>
                <strong>{t("notificationManager.totalUsers")}:</strong>{" "}
                {notificationToDelete.total_recipients || 0}
              </p>
              <p>
                <strong>{t("common.status")}:</strong>
                <Tag
                  color={getStatusColor(notificationToDelete.status)}
                  style={{ marginLeft: 8 }}
                >
                  {notificationToDelete.status ||
                    t("notificationManager.statusSent")}
                </Tag>
              </p>
              <p>
                <strong>{t("notificationManager.sentAt")}:</strong>{" "}
                {formatDate(notificationToDelete.created_at)}
              </p>
            </div>
            <p className="warning-text">
              {t("notificationManager.deleteWarning")}
            </p>
          </div>
        </Modal>
      )}
    </>
  );
};

export default NotificationManager;
